import { useWorkspaceId } from "../../hooks/useWorkspaceId";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { ResponseStatus } from "../use-add-custom-fulfilment/useAddCustomFulfilment";
import { useHttpTaxManagerAppHeaders } from "../utils/useHttpTaxManagerAppHeaders";
import { BASE_API_URL, HttpError } from "../utils/useTaxManagerAppQueryClient";

interface UpdateCustomFulfilmentRequest {
    deadline: Date;
    fulfilmentName: string;
    levyId: string;
}

export function useUpdateCustomFulfilment() {
    const { t } = useTranslation();
    const workspaceId = useWorkspaceId();
    const headers = useHttpTaxManagerAppHeaders({ method: "PATCH" });
    const queryClient = useQueryClient();

    const mutation = useMutation<
        ResponseStatus,
        Error,
        UpdateCustomFulfilmentRequest
    >({
        mutationFn: async (request) => {
            const response = await fetch(
                `${BASE_API_URL}/tenants/${workspaceId}/fulfilments/custom/${request.levyId}`,
                {
                    headers,
                    method: "PATCH",
                    body: JSON.stringify({
                        deadline: dayjs(request.deadline)
                            .hour(12)
                            .minute(0)
                            .second(0)
                            .millisecond(0)
                            .toISOString(),
                        fulfilmentName: request.fulfilmentName,
                    }),
                },
            );

            if (response.status === ResponseStatus.CONFLICT) {
                return ResponseStatus.CONFLICT;
            }

            if (!response.ok) {
                throw new HttpError(response);
            }

            return ResponseStatus.OK;
        },
        onSuccess: (status) => {
            if (status !== ResponseStatus.OK) return;
            queryClient.invalidateQueries({ queryKey: ["useGetFulfilments"] });
            queryClient.invalidateQueries({
                queryKey: ["useGetLinkableFulfilments"],
            });
            queryClient.invalidateQueries({ queryKey: ["useGetKPIs"] });
        },
        meta: {
            errorMessage: t("errors.messages.updateCustomFulfilment"),
        },
    });

    return mutation;
}
